import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { UserObjectsService } from '../../../core/services/user-objects.service';
import { DataObject } from '../../../core/models/core.models';
import { NomObjectClass } from '../../../core/constants';
import { augmentObjectWithIconAndImage } from '../../utils/object-utils';

import { Subject } from 'rxjs';
import { debounceTime, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-selected-data-source',
  templateUrl: './selected-data-source.component.html',
  styleUrls: ['./selected-data-source.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SelectedDataSourceComponent implements OnInit {
  @ViewChild('searchInput') searchInput: ElementRef;
  @Input() datasource: DataObject;
  @Input() objectTypeIds: number[] = [];
  @Output() datasourceChange = new EventEmitter<DataObject>();

  suggestions: DataObject[] = [];
  suggestionsActive = false;
  selectedSuggestIndex = -1;
  typedQuery = '';
  isLoading = false;

  private searchSubject = new Subject<string>();
  private destroyRef = inject(DestroyRef);

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private eRef: ElementRef,
    private userObjects: UserObjectsService,
  ) {}

  ngOnInit() {
    this.datasource = augmentObjectWithIconAndImage(this.datasource);

    this.searchSubject
      .pipe(
        debounceTime(300),
        tap(() => (this.isLoading = true)),
        switchMap((query) =>
          this.userObjects.search$({
            query,
            workspaceId: null,
            objectClassIds: [NomObjectClass.FEED],
            objectTypeIds: this.objectTypeIds,
            lean: true,
          }),
        ),
        tap((results) => {
          this.suggestions = results
            .filter((obj) => obj.id !== this.datasource?.id)
            .map((obj) => augmentObjectWithIconAndImage(obj));
          this.selectedSuggestIndex = this.suggestions.length > 0 ? 0 : -1;
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeSuggestions();
    }
  }

  onInput(value: string) {
    this.typedQuery = value;
    if (value.length > 0) {
      this.isLoading = true;
      this.suggestions = [];
      this.suggestionsActive = true;
      this.searchSubject.next(value);
    } else {
      this.closeSuggestions();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.suggestionsActive || this.suggestions.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.selectedSuggestIndex < this.suggestions.length - 1) {
          this.selectedSuggestIndex++;
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.selectedSuggestIndex > 0) {
          this.selectedSuggestIndex--;
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (this.selectedSuggestIndex >= 0) {
          this.selectSuggestion(this.suggestions[this.selectedSuggestIndex]);
        }
        break;

      case 'Escape':
        this.closeSuggestions();
        break;
    }
  }

  selectSuggestion(suggestion: DataObject) {
    this.datasourceChange.emit(suggestion);
    this.closeSuggestions();
  }

  closeSuggestions() {
    this.suggestionsActive = false;
    this.suggestions = [];
    this.selectedSuggestIndex = -1;
    this.typedQuery = '';
  }
}
